const normalizeText = (value) => String(value || '').trim().toLowerCase();

const includesAny = (haystack, keywords = []) => {
  if (!haystack) return false;
  return keywords.some((kw) => haystack.includes(kw));
};

const collator = typeof Intl !== 'undefined'
  ? new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' })
  : null;

const compareText = (a = '', b = '') => {
  const left = String(a);
  const right = String(b);
  if (collator) return collator.compare(left, right);
  return left.localeCompare(right, undefined, { numeric: true, sensitivity: 'base' });
};

const isNumericOnly = (value) => /^\d+$/.test(value || '');

const getLaneText = (item) => normalizeText(item?.extension_attributes?.lane);
const getZoneText = (item) => {
  const z = normalizeText(item?.extension_attributes?.zone);
  if (isNumericOnly(z)) return ''; // ignore pure numbers for ordering
  return z;
};

const getCategoryInfo = (item) => {
  const lane = getLaneText(item);
  const zone = getZoneText(item);
  const name = normalizeText(item?.name);
  const qtyOrdered = Number(item?.qty_ordered) || 0;
  const combined = `${lane} ${zone}`.trim();

  const frozenHit =
    includesAny(lane, ['frozen', 'freezer']) ||
    includesAny(zone, ['frozen', 'freezer']) ||
    includesAny(name, ['frozen', 'freezer']);

  const wholesaleHit =
    includesAny(lane, ['wholesale', 'warehouse']) ||
    includesAny(zone, ['wholesale', 'warehouse']) ||
    /\[case\]/i.test(String(item?.name || '')) ||
    qtyOrdered >= 12;

  if (wholesaleHit) {
    return { key: 'wholesale', priority: 25, subPriority: 0, label: 'Warehouse' };
  }

  if (frozenHit) {
    return { key: 'frozen', priority: 24, subPriority: 0, label: 'Frozen' };
  }

  const isEcom = includesAny(combined, [
    'ecom',
    'area 1',
    'area1',
    'area 2',
    'area2',
    'ecom 1',
    'ecom1',
    'ecom 2',
    'ecom2',
  ]);
  if (isEcom) {
    const area1 = includesAny(combined, ['area 1', 'area1', 'ecom 1', 'ecom1']);
    const area2 = includesAny(combined, ['area 2', 'area2', 'ecom 2', 'ecom2']);
    const subPriority = area1 ? 1 : area2 ? 2 : 3;
    return { key: 'ecom', priority: 1, subPriority, label: 'Ecom' };
  }

  const CATEGORY_RULES = [
    { key: 'rice', priority: 2, keywords: ['rice'], label: 'Rice' },
    { key: 'noodles', priority: 3, keywords: ['noodles', 'noodle'], label: 'Noodles' },
    { key: 'europe', priority: 4, keywords: ['europe', 'european'], label: 'Europe' },
    { key: 'nestle', priority: 5, keywords: ['nestle'], label: 'Nestle' },
    { key: 'flour', priority: 6, keywords: ['flour'], label: 'Flour' },
    { key: 'viet', priority: 8, keywords: ['viet', 'vietnam', 'vietnan', 'vietnamese'], label: 'Vietnam' },
    { key: 'malaysian', priority: 9, keywords: ['malaysian', 'malaysia', 'malaisia', 'malay'], label: 'Malaysian' },
    { key: 'thai', priority: 10, keywords: ['thai', 'thailand', 'thailander'], label: 'Thai' },
    { key: 'sauces', priority: 11, keywords: ['sauces', 'sauce'], label: 'Sauces' },
    { key: 'hotpot', priority: 12, keywords: ['hotpot', 'hot pot'], label: 'Hotpot' },
    { key: 'philippines', priority: 13, keywords: ['philippine', 'philippines', 'filipino', 'filipina'], label: 'Philippines' },
    { key: 'front-store', priority: 14, keywords: ['front store', 'front'], label: 'Front Store' },
    { key: 'drinks', priority: 15, keywords: ['drinks', 'drink'], label: 'Drinks' },
    { key: 'fresh', priority: 16, keywords: ['fresh', 'fridge', 'chill'], label: 'Fresh / Fridge / Chill' },
    { key: 'japanese', priority: 17, keywords: ['japanese', 'japan'], label: 'Japanese' },
    { key: 'korean', priority: 18, keywords: ['korean', 'korea'], label: 'Korean' },
    { key: 'china', priority: 19, keywords: ['china', 'chinese'], label: 'China' },
    { key: 'coffee-tea', priority: 20, keywords: ['coffee and tea', 'tea and coffee', 'coffee', 'tea'], label: 'Coffee and Tea' },
    { key: 'snacks', priority: 21, keywords: ['snacks', 'snack'], label: 'Snacks' },
    { key: 'homeware', priority: 22, keywords: ['homeware'], label: 'Homeware' },
  ];

  for (const rule of CATEGORY_RULES) {
    if (includesAny(lane, rule.keywords) || includesAny(zone, rule.keywords)) {
      return { key: rule.key, priority: rule.priority, subPriority: 0, label: rule.label };
    }
  }

  const indiaKeywords = ['india', 'indian', 'patak', "patak's", 'pataks', 'khanum', 'east end', 'heera'];
  if (includesAny(lane, indiaKeywords) || includesAny(zone, indiaKeywords)) {
    let subPriority = 5;
    if (includesAny(combined, ['patak', "patak's", 'pataks'])) subPriority = 1;
    else if (includesAny(combined, ['khanum'])) subPriority = 2;
    else if (includesAny(combined, ['east end'])) subPriority = 3;
    else if (includesAny(combined, ['heera'])) subPriority = 4;
    return { key: 'india', priority: 7, subPriority, label: 'India' };
  }

  if (includesAny(lane, ['alcohol']) || includesAny(zone, ['alcohol'])) {
    return { key: 'alcohol', priority: 23, subPriority: 0, label: 'Alcohol' };
  }

  if (includesAny(name, ['fresh'])) {
    return { key: 'fresh', priority: 16, subPriority: 0, label: 'Fresh / Fridge / Chill' };
  }

  return { key: 'other', priority: 0, subPriority: 0, label: 'Other' };
};

const isFrozenItem = (item) => getCategoryInfo(item).key === 'frozen';
const isWholesaleItem = (item) => getCategoryInfo(item).key === 'wholesale';

const compareItems = (a, b) => {
  const aCat = getCategoryInfo(a);
  const bCat = getCategoryInfo(b);

  if (aCat.priority !== bCat.priority) {
    return aCat.priority - bCat.priority;
  }
  if (aCat.subPriority !== bCat.subPriority) {
    return aCat.subPriority - bCat.subPriority;
  }

  const laneDiff = compareText(getLaneText(a), getLaneText(b));
  if (laneDiff !== 0) return laneDiff;

  const zoneDiff = compareText(getZoneText(a), getZoneText(b));
  if (zoneDiff !== 0) return zoneDiff;

  return compareText(a.name || '', b.name || '');
};

const sortOrderItems = (items = []) => {
  const sorted = [...items];
  sorted.sort(compareItems);
  return sorted;
};

export {
  sortOrderItems,
  getCategoryInfo,
  isFrozenItem,
  isWholesaleItem,
  getLaneText,
  getZoneText,
  compareText,
  compareItems,
};
